import { useCallback, useRef, useState } from 'react';
import type { TextDraft } from './markupDrafts';
import { newShapeId, type Point, type TextShape } from './markupShapes';

export interface TextDraftControls {
    draft: TextDraft | null;
    place: (at: Point, color: string, fontSize: number) => void;
    edit: (shape: TextShape) => void;
    change: (value: string) => void;
    commit: () => void;
    cancel: () => void;
}

// The ref is the source of truth so a blur arriving after an explicit commit is a no-op.
export function useTextDraft(onCommit: (draft: TextDraft) => void): TextDraftControls {
    const draftRef = useRef<TextDraft | null>(null);
    const [draft, setDraft] = useState<TextDraft | null>(null);

    const update = useCallback((next: TextDraft | null) => {
        draftRef.current = next;
        setDraft(next);
    }, []);

    const place = useCallback(
        (at: Point, color: string, fontSize: number) =>
            update({ id: newShapeId(), at, color, fontSize, value: '', editing: false }),
        [update]
    );

    const edit = useCallback(
        (shape: TextShape) =>
            update({
                id: shape.id,
                at: shape.at,
                color: shape.color,
                fontSize: shape.fontSize,
                value: shape.text,
                editing: true
            }),
        [update]
    );

    const change = useCallback(
        (value: string) => {
            const current = draftRef.current;
            if (current) update({ ...current, value });
        },
        [update]
    );

    const commit = useCallback(() => {
        const current = draftRef.current;
        if (!current) return;
        update(null);
        onCommit(current);
    }, [onCommit, update]);

    const cancel = useCallback(() => update(null), [update]);

    return { draft, place, edit, change, commit, cancel };
}
